import { useEffect, useRef } from 'react';

import {
  consumePendingChatSend,
  setChatPrefill,
  setPendingChatSend,
  useChatAppContextInputs,
  type ChatPendingSendTarget,
} from '@/chatContext/chatAppContextStore';
import type { ChatStreamOutcome } from '@/services/chatStream/chatStreamClient';
import type { ChatSendInput } from './useChatSend';

/** Refusals of one ask before it is handed to the composer instead. */
const MAX_SEND_ATTEMPTS = 3;

export interface UsePendingChatSendOptions {
  /** This mounted surface may claim only requests explicitly addressed to it. */
  target: ChatPendingSendTarget | null;
  /** Hidden shells stay subscribed, but must not claim a request. */
  enabled: boolean;
  /** Thread resolution must finish before a staged ask chooses a conversation. */
  isThreadReady: boolean;
  /**
   * A turn cannot be opened yet but will be able to be: one is streaming, the
   * one before it is still settling, or the thread is still resolving. Claiming
   * through any of those would spend the request on a send the path refuses.
   */
  isBusy: boolean;
  /** The daily allowance is spent, so the send path would refuse the turn. */
  isLimitReached: boolean;
  send: (input: ChatSendInput) => Promise<ChatStreamOutcome | null>;
}

/**
 * Deliver a producer-staged ask once its addressed surface can send it.
 *
 * Consumption happens before invoking `send`: this makes Strict Mode and
 * rerenders exactly-once. A claim cannot be undone, so it is made only once
 * the send path will actually take the turn: `isBusy` covers every transient
 * reason it would refuse — a turn streaming, the turn before it still settling
 * after `turn.done`, a thread still resolving — and `isLimitReached` covers the
 * one refusal that is not transient.
 *
 * A `send` that still resolves null was refused for a reason the gate could not
 * see, so the ask goes back on the stack and the next render tries again. Only
 * a refusal that survives `MAX_SEND_ATTEMPTS` is treated as settled — the daily
 * cap, say — and falls back to the composer prefill, where the student keeps
 * their question and the composer explains itself.
 */
export const usePendingChatSend = ({
  target,
  enabled,
  isThreadReady,
  isBusy,
  isLimitReached,
  send,
}: UsePendingChatSendOptions): void => {
  const { pendingSend } = useChatAppContextInputs();
  /** Refusals of the ask now in hand, so a retry cannot become a loop. */
  const attempts = useRef({ message: '', count: 0 });

  useEffect(() => {
    if (
      !enabled ||
      !target ||
      !isThreadReady ||
      isBusy ||
      isLimitReached ||
      pendingSend?.target !== target
    ) {
      return;
    }

    const claimed = consumePendingChatSend(pendingSend.id);
    if (!claimed) return;

    attempts.current =
      attempts.current.message === claimed.message
        ? { message: claimed.message, count: attempts.current.count + 1 }
        : { message: claimed.message, count: 1 };

    const attempt = attempts.current.count;

    void send({ message: claimed.message, entry: 'staged' }).then((outcome) => {
      if (outcome) return;

      if (attempt < MAX_SEND_ATTEMPTS) {
        setPendingChatSend(claimed.message, claimed.target);
        return;
      }

      setChatPrefill(claimed.message);
    });
  }, [
    enabled,
    isBusy,
    isLimitReached,
    isThreadReady,
    pendingSend,
    send,
    target,
  ]);
};
